/**
 * PROJECT TOOLTIP
 * 
 * Lightweight tooltip that appears on hover over projects.
 */

import { useWorldStore } from "@/stores/worldStore";
import { useSelectionStore } from "@/stores/selectionStore";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Clock, Users } from "lucide-react";
import { useEffect, useState } from "react";

const statusColors = {
  healthy: "bg-green-500/10 text-green-600 border-green-500/20",
  degraded: "bg-amber-500/10 text-amber-600 border-amber-500/20",
  failing: "bg-red-500/10 text-red-600 border-red-500/20",
};

export function ProjectTooltip() {
  const { projects, agents } = useWorldStore();
  const { hoveredProjectId } = useSelectionStore();
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePos({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  if (!hoveredProjectId) return null;

  const project = projects.find((p) => p.id === hoveredProjectId);
  if (!project) return null;

  const projectAgents = agents.filter((a) => a.projectId === project.id);
  const busyCount = projectAgents.filter((a) => a.state === "busy").length;
  const idleCount = projectAgents.filter((a) => a.state === "idle").length;
  const queuedCount = projectAgents.filter((a) => a.state === "queued").length;

  return (
    <Card
      className="fixed z-[100] p-3 min-w-[220px] pointer-events-none shadow-lg"
      style={{
        left: mousePos.x + 15,
        top: mousePos.y + 15,
      }}
      data-testid="tooltip-project"
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <h3 className="font-semibold text-sm">{project.name}</h3>
        <Badge variant="outline" className={`text-xs capitalize ${statusColors[project.status]}`}>
          {project.status}
        </Badge>
      </div>

      {project.description && (
        <p className="text-xs text-muted-foreground mb-3">{project.description}</p>
      )}

      <div className="flex items-center gap-4 text-xs text-muted-foreground">
        <div className="flex items-center gap-1">
          <Users className="w-3 h-3" />
          <span>{projectAgents.length} agents</span>
        </div>
        {project.lastDeployed && (
          <div className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            <span>{project.lastDeployed}</span>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 mt-2 pt-2 border-t">
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 rounded-full bg-slate-400" />
          <span className="text-xs text-muted-foreground">{idleCount}</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 rounded-full bg-blue-500" />
          <span className="text-xs text-muted-foreground">{busyCount}</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 rounded-full bg-purple-500" />
          <span className="text-xs text-muted-foreground">{queuedCount}</span>
        </div>
      </div>
    </Card>
  );
}
